import React, { useState, useEffect } from "react";
import { Formik, Field, FormikActions } from "formik";
import {
  required,
  isPhone,
  isYear,
  checkLength,
  isEmail,
  isRelevantDate,
} from "utils/formValidators";
import { normalizePhone, getLocalDate } from "utils/helpers";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { IRootState } from "reducers";
import { Actions, ICartProduct, getPrices, getCartPrices } from "reducers/cart";
import { getRefBonus } from "reducers/user";
import {normalizePrice, setEmptyValuesToNull, getPureDate} from "utils/helpers";
import styled from '@emotion/styled/macro';

import { Actions as appActions } from "reducers/app";

import CartProduct from "./components/CartProduct";
import TextField from "components/TextField";
import { DateField } from "components/DatePicker";
import SelectField, { IOption } from "components/SelectField";
import ImagePicker from "./components/ImagePicker";

import { getServiceCenters, orderAdd, sendOfferPdf } from "api/regRequest";
import { push } from "connected-react-router";

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import './tabsStyles.css';
import { USER_ROLES } from "enums/userRoles";

const CustomTabs = styled(Tabs)({
  marginTop: 40,
});

const TabPanelWrapper = styled.div({
  marginTop: 20,
});

const AddressWrapper = styled.div({
  padding: "0px 5px",
  width: "100%",
});

interface IProps {
  products: ICartProduct[];
  prices: { [key: string]: number };
  discountSumm: any;
  goTo: any;
  fetchCartSuccess: (payload: ICartProduct[]) => void;
  toggleUIBlocking: (payload: boolean) => void;
  cartPrices: {
    no: number;
    market_price: number;
    price: number;
    bonus: number;
    summ: number;
    bonusWithoutDiscount: number;
    error: boolean;
  }[];
  userRole: number;
}

interface IForm {
  client_first_name: string;
  client_second_name: string;
  client_patronymic: string;
  client_phonenumber: string;
  service_center_id?: number | string;
  car_brand: string;
  car_model: string;
  photo?: File;
  car_vin: string;
  car_prod_year: string;
  car_color: string;
  client_email: string;
  address: string;
  object_type?: any;
  installation_date: any;
  comment: string;
}

const objectTypes = [{
  id: "Малый бизнес",
  name: "Малый бизнес",
}, {
  id: "Квартира",
  name: "Квартира",
}, {
  id: "Коттедж",
  name: "Коттедж",
}];

const initialValues = {
  client_first_name: "",
  client_second_name: "",
  client_patronymic: "",
  client_phonenumber: "",
  service_center_id: "",
  car_brand: "",
  car_model: "",
  car_vin: "",
  car_prod_year: "",
  car_color: "",
  client_email: "",
  address: "",
  object_type: "",
  installation_date: "",
  comment: "",
};

const CartPage: React.SFC<IProps> = ({
  products,
  prices,
  cartPrices,
  fetchCartSuccess,
  goTo,
  toggleUIBlocking,
  userRole,
}) => {
  const [services, setServices] = useState<IOption[]>([]);
  useEffect(() => {
    getServiceCenters().then((response: any) => {
      if (response.success) {
        setServices(response.data);
      } else {
        toastr.error("Ошибка загрузки сервисных центров", response.error);
      }
    });
  }, []);

  /**
   * Оформление заказа
   */
  const submit = (
    values: IForm,
    { setSubmitting, resetForm }: FormikActions<IForm>
  ) => {
    if (cartPrices.some(i => i.error)) {
      toastr.error("Скидка превышает бонус. Уменьшите размер скидки.", "");
    } else {
      const convertedValues = {
        ...values,
        client_phonenumber: `+${normalizePhone(values.client_phonenumber)}`,
        car_prod_year: values.car_prod_year
          ? parseInt(values.car_prod_year)
          : undefined,
        products: products.map(i => {
          const prodPrices = cartPrices.find(el => el.no === i.no);
          return {
            product_id: i.product_id,
            quantity: i.quantity,
            discount: parseFloat(i.discount) || 0,
            car_class_id: i.car_classes_id,
            price_each: prodPrices!.price,
            agent_fee: prodPrices!.bonus
          };
        }),
        order_price: prices.price,
        object_type: values.object_type ? values.object_type : null,
        service_center_id: values.service_center_id ? values.service_center_id : null,
      };
      const finalValues = setEmptyValuesToNull(convertedValues);
      if (!finalValues.client_patronymic) finalValues.client_patronymic = '';
      toggleUIBlocking(true);
      orderAdd(finalValues).then((response: any) => {
        if (response.success) {
          fetchCartSuccess([]);
          resetForm();
          toastr.success("Заказ успешно сформирован", "");
          goTo("/home/catalog/page/0");
        } else {
          toastr.error(response.error || "Ошибка оформления заявки", "");
        }
        toggleUIBlocking(false);
      });
    }
  };

  /**
   * Отправка КП клиенту
   */
  const sendOffer = ({
    client_first_name,
    client_second_name,
    client_patronymic,
    client_email
  }: IForm) => {
    if (cartPrices.some(i => i.error)) {
      toastr.error("Скидка превышает бонус. Уменьшите размер скидки.", "");
      return;
    }

    if (!client_first_name) {
      toastr.error("Введите Имя клиента", "");
      return;
    }

    if (isEmail(client_email)) {
      toastr.error("Введите валидный e-mail клиента", "");
      return;
    }

    const convertedValues = {
      client_first_name,
      client_second_name,
      client_patronymic,
      client_email,
      date: getLocalDate(new Date(), "DD-MM-YYYY"),
      products: products.map(i => {
        const prodPrices = cartPrices.find(el => el.no === i.no);
        const carClassEl = i.product_price_classes.find((el: any) => el.car_class_id === i.car_classes_id);
        return {
          product_id: i.product_id,
          quantity: i.quantity,
          discount_each:
            Math.round(((parseFloat(i.discount) || 0) / i.quantity) * 100) /
            100,
          market_price: prodPrices!.market_price,
          price_each: prodPrices!.price,
          carClass: carClassEl!.car_class_name
        };
      })
    };

    toggleUIBlocking(true);
    sendOfferPdf(convertedValues)
      .then((response: any) => {
        if (response.success) {
          toastr.success("Предложение отправлено клиенту", "");
        } else {
          toastr.error("Ошибка отправка КП", response.error || "");
        }
        toggleUIBlocking(false);
      })
      .catch(() => toggleUIBlocking(false));
  };

  const getVehicleInputs = (values: any, setFieldValue: any) => (
    <React.Fragment>
      <div className="row inf-text">
        <div className="col-md-4 col-sm-12">
          <p>Марка</p>
          <Field
            component={TextField}
            name="car_brand"
            validate={required}
          />
        </div>
        <div className="col-md-4 col-sm-12">
          <p>Модель</p>
          <Field
            component={TextField}
            name="car_model"
            validate={required}
          />
        </div>
        <div className="col-md-4 col-sm-12">
          <p>VIN-номер</p>
          <Field
            component={TextField}
            name="car_vin"
            validate={checkLength(17)}
            onChange={(e: any) => {
              const val = e.target.value;
              const newValue = !/^[\d\w]{0,17}$/.test(val)
                ? values.car_vin
                : val;
              setFieldValue("car_vin", newValue);
            }}
          />
        </div>
      </div>
      <div className="row inf-text">
        <div className="col-md-4 col-sm-12">
          <p>Цвет</p>
          <Field component={TextField} name="car_color" />
        </div>
        <div className="col-md-4 col-sm-12">
          <p>Год выпуска</p>
          <Field
            mask="9999"
            component={TextField}
            name="car_prod_year"
            validate={isYear}
          />
        </div>
        <div className="col-md-4 col-sm-12 sel-block plan___f__">
          <p>Сервисный центр</p>
          <Field
            component={SelectField}
            options={services}
            name="service_center_id"
            placeholder="Выберите сервисный центр"
            validate={required}
          />
        </div>
      </div>
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <div className="container">
        <div className="row">
          <div className="col-12 mb-3">
            <h2 className="title">Корзина</h2>

            {products.length ? (
              <div className="d-none d-xl-block">
                <div className="t d-flex justify-content-around">
                  <div className="col-xl-5 col-lg-6"> Наименование</div>

                  <span> Цена, ₽</span>

                  <span> Класс</span>

                  <span> Кол-во</span>

                  <span className="sale__"> Сумма скидки, ₽</span>

                  <span> Бонус</span>

                  <span> Сумма, ₽ </span>
                </div>
              </div>
            ) : null}
          </div>
          <TransitionGroup className="cart-products">
            {products.map(item => (
              <CSSTransition
                key={item.no}
                classNames="cart-products"
                timeout={{ enter: 500, exit: 500 }}
              >
                <CartProduct
                  {...item}
                  cartPrices={cartPrices.find(i => i.no === item.no)}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
        <div className="row">
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 margin pl-0 media_none___  order-last order-md-first sm-pd-r">
            <div className="cont-itog card bottom______ lq">
              <div className="text_discount">
                <small>Скидка:</small>
                <p>{normalizePrice(prices.discount)} ₽</p>
              </div>
              <span className="microtext___ d-md-none d-lg-block">
                *Скидка формируется
                <span className="d-md-block d-lg-none" /> за счет гонарара
                агента
              </span>
            </div>
            <span className="microtext___ d-none d-md-block d-lg-none">
              *Скидка формируется
              <span className="d-block d-lg-none" /> за счет гонарара агента
            </span>
          </div>
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 margin  pl-0 media_none___  sm-pd-r">
            <div className="cont-itog card bottom______ ">
              <div className="text_discount green_text_">
                <small>Ваш бонус:</small>
                <p>
                  {normalizePrice(prices.bonus - prices.discount)}
                </p>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 margin  pl-0 pl-0 pr-0 media_none___ order-first order-md-last">
            <div className="cont-itog card bottom______ with_border__">
              <div className="text_discount total__">
                <small>К оплате клиенту:</small>
                <p>
                  {normalizePrice(prices.price - prices.discount)} ₽
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="row ">
          <div className="col-12 card info_card__">
            <Formik
              initialValues={{
                ...initialValues,
                installation_date: userRole === USER_ROLES.CAR_AGENT ? null : new Date(),
              }}
              onSubmit={submit}
              render={({ values, submitForm, setFieldValue, resetForm }) => (
                <div className="cont">
                  <div className=" inf-pad">
                    <h4>Информация о клиенте</h4>
                  </div>
                  <div className="row inf-text">
                    <div className="col-12 col-md-6 col-sm-12">
                      <p>Фамилия</p>
                      <Field
                        component={TextField}
                        name="client_second_name"
                        validate={required}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-sm-12">
                      <p>Имя</p>
                      <Field
                        component={TextField}
                        name="client_first_name"
                        validate={required}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-sm-12">
                      <p>Отчество</p>
                      <Field
                        component={TextField}
                        name="client_patronymic"
                      />
                    </div>
                    <div className="col-12 col-md-6 col-sm-12">
                      <p>Номер телефона</p>
                      <Field
                        type="tel"
                        name="client_phonenumber"
                        validate={isPhone}
                        mask="+7 (999) 999-99-99"
                        component={TextField}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-sm-12">
                      <p>E-mail</p>
                      <Field name="client_email" component={TextField} />
                    </div>
                    <div className="col-12 col-md-6 col-sm-12 ">
                      <button
                        className="btn mt-2 btn-send-KP"
                        type="submit"
                        onClick={() => sendOffer(values)}
                        disabled={!products.length}
                      >
                        Отправить предложение
                      </button>
                    </div>
                  </div>
                  { userRole === USER_ROLES.CAR_AGENT
                    ? <React.Fragment>
                        <div className="  inf-pad">
                          <h4>Информация об авто</h4>
                        </div>
                        { getVehicleInputs(values, setFieldValue) }
                      </React.Fragment>
                    : <CustomTabs onSelect={(index: number, lastIndex: number) => { 
                        if (index !== lastIndex) {
                          const updatedValues: IForm = {
                            ...initialValues,
                            client_second_name: values.client_second_name,
                            client_first_name: values.client_first_name,
                            client_patronymic: values.client_patronymic,
                            client_phonenumber: values.client_phonenumber,
                            client_email: values.client_email,
                          };
                          if (index === 0) {
                            updatedValues.installation_date = new Date();
                          } else {
                            updatedValues.installation_date = null;
                          }
                          resetForm(updatedValues);
                        }
                      }}>
                        <TabList>
                          <Tab>
                            <h4>Информация об объекте</h4>
                          </Tab>
                          <Tab>
                            <h4>Информация об авто</h4>
                          </Tab>
                        </TabList>
                        <TabPanel>
                          <TabPanelWrapper>
                            <div className="row inf-text">
                              <AddressWrapper>
                                <p>Адрес объекта</p>
                                <Field
                                  component={TextField}
                                  name="address"
                                  validate={required}
                                />
                              </AddressWrapper>
                            </div>
                            <div className="row inf-text">
                              <div className="col-md-4 col-sm-12 sel-block plan___f__">
                                <p>Тип объекта</p>
                                <Field
                                  component={SelectField}
                                  options={objectTypes}
                                  name="object_type"
                                  placeholder="Выберите тип объекта"
                                  validate={required}
                                />
                              </div>
                              <div className="col-md-4 col-sm-12">
                                <p>Желаемая дата установки</p>
                                <Field
                                  component={DateField}
                                  name="installation_date"
                                  validate={isRelevantDate}
                                />
                              </div>
                              <div className="col-md-4 col-sm-12">
                                <p>Комментарий</p>
                                <Field
                                  component={TextField}
                                  name="comment"
                                />
                              </div>
                            </div>
                          </TabPanelWrapper>
                        </TabPanel>
                        <TabPanel>
                          <TabPanelWrapper>
                            { getVehicleInputs(values, setFieldValue) }
                          </TabPanelWrapper>
                        </TabPanel>
                      </CustomTabs>
                  }
                  <div className="row cart-drag-zone">
                    <Field name="photo" component={ImagePicker} />
                  </div>
                  <div className="row sm-btn">
                    <button
                      className="btn mt-2 mx-auto btn_auto"
                      type="submit"
                      onClick={submitForm}
                      disabled={!products.length}
                    >
                      Оформить заявку
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = ({ cart: { products }, user }: IRootState) => {
  const refBonus = getRefBonus(user);
  return {
    products,
    prices: getPrices(products, refBonus),
    cartPrices: getCartPrices(products, refBonus),
    userRole: user.role_id!,
  };
};

const actions = {
  fetchCartSuccess: Actions.fetchCartSuccess,
  goTo: push,
  toggleUIBlocking: appActions.toggleUIBlocking
};

export default connect(
  mapStateToProps,
  actions
)(CartPage);
